import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TableModule} from 'primeng/table';
import {ButtonModule} from 'primeng/button';
import {CheckboxModule} from 'primeng/checkbox';
import {PaginatorModule} from 'primeng/paginator';
import {TooltipModule} from 'primeng/tooltip';
import {FormsModule} from '@angular/forms';
import {ResourceComponent} from "@components/select/resource-component";
import {ServerTranslateService} from '@services/server-translate.service';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {MsfNumber} from '@app/utils/pipes/format-number';

export interface TableColumn {
  field: string;
  header: string;
  headerSubtitle?: string;
  type?: 'text' | 'double' | 'number' | 'date' | 'badge' | 'boolean' | 'actions' | 'custom' | 'localizationString'| 'fileSize';
  width?: string;
  minWidth?: string;
  sortable?: boolean;
  customTemplate?: any;
  badgeClass?: (value: any) => string;
  defaultSorted?: boolean;
  rowspan?: number;
  colspan?: number;
  children?: TableColumn[];
  data?: any;
  parseFunction?: (row: any, translateService?: TranslateService) => any;
}

export interface TableConfig {
  selectable?: boolean;
  paginator?: boolean;
  rows?: number;
  rowsPerPageOptions?: number[];
  loading?: boolean;
  emptyMessage?: string;
  globalFilter?: boolean;
  actions?: TableAction[];
  keepOrder?: boolean; // when true, preserve incoming data order without sorting
}

export interface TableAction {
  icon: string;
  tooltip?: string;
  command: (row: any) => void;
  visible?: (row: any) => boolean;
  class?: string;
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    CheckboxModule,
    PaginatorModule,
    TooltipModule,
    FormsModule,
    MsfNumber,
    DateFormatLangPipe
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
  providers:[
    DatePipe,
    DateFormatLangPipe,
    ServerTranslateService
  ],
})
export class DataTableComponent extends ResourceComponent implements OnInit {
  @Input() columns: TableColumn[] = [];
  //@ts-ignore
  @Input() data: any[] = [];
  @Input() config: TableConfig = {};
  @Input() override totalRecords: number = 0;
  @Input() override loading: boolean = false;

  @Output() onRowSelect = new EventEmitter<any>();
  @Output() onRowClick = new EventEmitter<any>();
  @Output() onRowUnselect = new EventEmitter<any>();
  @Output() onSort = new EventEmitter<any>();
  @Output() onPage = new EventEmitter<any>();
  @Output() onFilter = new EventEmitter<any>();
  @Output() onSelectAllChange = new EventEmitter<boolean>();


  selectedRows: any[] = [];
  selectAll: boolean = false;
  rows: number = 10;
  override first: number = 0;

  constructor(
    public override cdr: ChangeDetectorRef,
    public dateFormatLang: DateFormatLangPipe,
    public tr: TranslateService,
    private serverTranslateService: ServerTranslateService,
  ) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
    if (this.config.rows) {
      this.rows = this.config.rows;
    }

    if (this.config.rowsPerPageOptions && this.config.rowsPerPageOptions.length > 0) {
      this.rows = this.config.rowsPerPageOptions[0];
    }

  }


  onRowClickChange(row: any): void {
    this.onRowClick.emit(row);
    if (this.config.selectable) {
      this.onRowSelect.emit(row);
    }
  }

    onPageChange(event: any): void {
        this.first = event.first;
        this.rows = event.rows;
        this.onPage.emit(event);
    }


  onSelectAllToggle(): void {
    if (this.selectAll) {
      this.selectedRows = [...this.data];
    } else {
      this.selectedRows = [];
    }
    this.onSelectAllChange.emit(this.selectAll);
  }

  onRowSelectChange(row: any): void {
    if (row.selected) {
      this.selectedRows.push(row);
      this.onRowSelect.emit(row);
    } else {
      this.selectedRows = this.selectedRows.filter(r => r !== row);
      this.onRowUnselect.emit(row);
    }

    this.selectAll = this.selectedRows.length === this.data.length;
  }

  formatDate(date: string | Date): string | any {
    return this.dateFormatLang.transform(date, this.tr.currentLang)
  }

  executeAction(action: TableAction, row: any): void {
    if (action.command) {
      action.command(row);
    }
  }

  isActionVisible(action: TableAction, row: any): boolean {
    return action.visible ? action.visible(row) : true;
  }

  get hasHeaderGroups(): boolean {
    return Array.isArray((this as any).columns) && (this as any).columns.some((c: any) => c.children && c.children.length > 0);
  }

  get hasColspan(): boolean {
    if (!this.columns || !Array.isArray(this.columns)) {
      return false;
    }
    return this.columns.some(col =>
      col && (col.colspan || (Array.isArray(col.children) && col.children.some(c => c && c.colspan)))
    );
  }

  /** Body cell count (grouped columns expand to one td per child). */
  get leafColumnCount(): number {
    if (!this.columns?.length) {
      return 0;
    }
    return this.columns.reduce((acc, col) => {
      if (col.children?.length) {
        return acc + col.children.length;
      }
      return acc + 1;
    }, 0);
  }

  getSortedData(): any[] {
    if (this.config?.keepOrder) {
      return this.data || [];
    }
    if (!this.columns || this.columns.length === 0) return this.data;

    const sortedCol = this.columns.find(col => col.defaultSorted) || this.columns[0];
    const field = sortedCol.field;
    const type = sortedCol.type;

    return [...this.data].sort((a, b) => {
      let valA = a[field];
      let valB = b[field];

      if (type === 'localizationString') {
        valA = this.getLocalizedValue(a, field);
        valB = this.getLocalizedValue(b, field);
      }

      if (valA == null) return 1;
      if (valB == null) return -1;

      // Для дат сортируем по убыванию (от новых к старым), чтобы сохранить порядок с бэкенда
      if (type === 'date') {
        const dateA = valA instanceof Date ? valA : new Date(valA);
        const dateB = valB instanceof Date ? valB : new Date(valB);
        return dateB.getTime() - dateA.getTime(); // Убывающий порядок
      }

      if (typeof valA === 'string' && typeof valB === 'string') {
        return valA.localeCompare(valB);
      }
      return valA > valB ? 1 : valA < valB ? -1 : 0;
    });
  }

  getCellValue(row: any, col: TableColumn): any {
    if (col.parseFunction) {
      return col.parseFunction(row, this.tr);
    }
    return row[col.field];
  }

  getLocalizedValue(row: any, field: string): string {
    return this.serverTranslateService.getDisplayValue(row, field);
  }

  formatCellText(value: unknown): string {
    if (this.serverTranslateService.isEmptyTranslationValue(value)) {
      return '';
    }

    if (typeof value === 'object' && value !== null) {
      return this.serverTranslateService.formatTranslationLabel(value);
    }

    return value == null ? '' : String(value);
  }
}
